import { useEffect, useState } from 'react';

/**
 * Global keyboard-shortcuts hook.
 *
 * Listens for `?` (without modifiers, when no input/textarea/contenteditable is
 * focused) to open the shortcuts modal, and `Escape` to close it.
 *
 * Returns `{ open, setOpen }` so a caller can render a controllable modal.
 */
export function useKeyboardShortcuts() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function isEditable(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (target.isContentEditable) return true;
      return false;
    }

    function onKey(e: KeyboardEvent) {
      // Ignore when typing in inputs, with modifiers, or during composition
      if (e.defaultPrevented) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditable(e.target)) return;

      if (e.key === '?') {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return { open, setOpen };
}
